import api from './api';
import type { VentaPendienteMaster } from './ventasSeguimientoService';
import { ordenTrabajoToPendientesMaster } from '../utils/ordenTrabajoFactura';

export interface OrdenReparacionItemDto {
  codigoArticulo: string;
  descripcion: string;
  cantidad: number;
  unidadMedida: string;
  nroItemMaster?: number;
  precioUnitario?: number;
  precioLista?: number;
  porcDesc?: number;
  pendienteFacturar?: number;
}

export interface CreateOrdenReparacionDto {
  nroCliente: number;
  nombreCliente?: string | null;
  direccionCliente?: string | null;
  nroDepositoOrigen: number | null;
  nombreDepositoOrigen?: string | null;
  nroVendedor: number | null;
  nroTransporte: number | string | null;
  nombreTransporte?: string | null;
  serie: string;
  numero: string;
  fechaEmision: string;
  horaEmision: string;
  fechaVence: string;
  detalleReparacion: string;
  /** Identificador de unidad (patente). Opcional. */
  patente?: string | null;
  items: OrdenReparacionItemDto[];
}

export type UpdateOrdenReparacionDto = CreateOrdenReparacionDto;

export interface OrdenReparacionReadDto extends CreateOrdenReparacionDto {
  id: number | string;
  nroFormulario?: number;
  estado?: string | null;
  facturada?: boolean;
}

export interface OrdenReparacionSearchParams {
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  estado?: string;
  facturada?: boolean;
  fechaDesde?: string;
  fechaHasta?: string;
  signal?: AbortSignal;
}

export interface OrdenReparacionPagedResult {
  items: OrdenReparacionReadDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

const str = (v: unknown): string => String(v ?? '').trim();

const numOrNull = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const normalizeItem = (raw: Record<string, unknown>): OrdenReparacionItemDto => ({
  codigoArticulo: str(raw.codigoArticulo ?? raw.CodigoArticulo ?? raw.codigo ?? raw.Codigo),
  descripcion: str(raw.descripcion ?? raw.Descripcion),
  cantidad: Number(raw.cantidad ?? raw.Cantidad ?? 1) || 1,
  unidadMedida: str(raw.unidadMedida ?? raw.UnidadMedida ?? raw.um ?? raw.UM) || 'UNI',
  nroItemMaster: (() => {
    const n = numOrNull(raw.nroItemMaster ?? raw.NroItemMaster);
    return n != null ? n : undefined;
  })(),
  precioUnitario: Number(raw.precioUnitario ?? raw.PrecioUnitario ?? raw.precio ?? raw.Precio) || undefined,
  precioLista: Number(raw.precioLista ?? raw.PrecioLista) || undefined,
  porcDesc: Number(raw.porcDesc ?? raw.PorcDesc ?? raw.porcDesc1 ?? raw.PorcDesc1) || undefined,
  pendienteFacturar: Number(raw.pendienteFacturar ?? raw.PendienteFacturar) || undefined,
});

const normalizeOrden = (raw: Record<string, unknown>): OrdenReparacionReadDto => {
  const itemsRaw = (raw.items ?? raw.Items ?? []) as unknown[];
  const idRaw = raw.id ?? raw.Id ?? raw.nroOrden ?? raw.NroOrden;
  const nroFormularioRaw = raw.nroFormulario ?? raw.NroFormulario ?? idRaw;
  return {
    id: idRaw != null && idRaw !== '' ? (idRaw as number | string) : '',
    nroFormulario: numOrNull(nroFormularioRaw) ?? undefined,
    estado: str(raw.estado ?? raw.Estado) || null,
    facturada: Boolean(raw.facturada ?? raw.Facturada ?? false),
    nroCliente: Number(raw.nroCliente ?? raw.NroCliente ?? 0) || 0,
    nombreCliente: str(raw.nombreCliente ?? raw.NombreCliente) || null,
    direccionCliente: str(raw.direccionCliente ?? raw.DireccionCliente) || null,
    nroDepositoOrigen: numOrNull(raw.nroDepositoOrigen ?? raw.NroDepositoOrigen),
    nombreDepositoOrigen: str(raw.nombreDepositoOrigen ?? raw.NombreDepositoOrigen) || null,
    nroVendedor: numOrNull(raw.nroVendedor ?? raw.NroVendedor),
    nroTransporte: (raw.nroTransporte ?? raw.NroTransporte ?? null) as number | string | null,
    nombreTransporte: str(raw.nombreTransporte ?? raw.NombreTransporte) || null,
    serie: str(raw.serie ?? raw.Serie) || 'A',
    numero: str(raw.numero ?? raw.Numero) || '',
    fechaEmision: str(raw.fechaEmision ?? raw.FechaEmision),
    horaEmision: str(raw.horaEmision ?? raw.HoraEmision),
    fechaVence: str(raw.fechaVence ?? raw.FechaVence),
    detalleReparacion: str(raw.detalleReparacion ?? raw.DetalleReparacion),
    patente: str(raw.patente ?? raw.Patente) || null,
    items: Array.isArray(itemsRaw)
      ? itemsRaw.map((i) => normalizeItem(i as Record<string, unknown>))
      : [],
  };
};

const normalizePagedOrdenes = (
  data: unknown,
  params: OrdenReparacionSearchParams,
): OrdenReparacionPagedResult => {
  if (data && typeof data === 'object' && ('items' in data || 'Items' in data)) {
    const paged = data as OrdenReparacionPagedResult & {
      Items?: unknown[];
      TotalCount?: number;
      PageNumber?: number;
      PageSize?: number;
      TotalPages?: number;
    };
    const list = (paged.items ?? paged.Items ?? []) as unknown[];
    return {
      items: list.map((i) => normalizeOrden(i as Record<string, unknown>)),
      totalCount: paged.totalCount ?? paged.TotalCount ?? 0,
      pageNumber: paged.pageNumber ?? paged.PageNumber ?? 1,
      pageSize: paged.pageSize ?? paged.PageSize ?? 10,
      totalPages: paged.totalPages ?? paged.TotalPages ?? 0,
    };
  }
  if (Array.isArray(data)) {
    const items = data.map((i) => normalizeOrden(i as Record<string, unknown>));
    return {
      items,
      totalCount: items.length,
      pageNumber: params.pageNumber ?? 1,
      pageSize: params.pageSize ?? 10,
      totalPages: 1,
    };
  }
  return {
    items: [],
    totalCount: 0,
    pageNumber: params.pageNumber ?? 1,
    pageSize: params.pageSize ?? 10,
    totalPages: 0,
  };
};

const normalizeProximoNumero = (data: unknown): string => {
  if (typeof data === 'string' || typeof data === 'number') return str(data);
  if (data && typeof data === 'object') {
    const raw = data as Record<string, unknown>;
    return str(raw.numero ?? raw.Numero ?? raw.proximoNumero ?? raw.ProximoNumero);
  }
  return '';
};

const prepareItemsForApi = (items: OrdenReparacionItemDto[]): OrdenReparacionItemDto[] =>
  items.map((it, idx) => ({
    codigoArticulo: it.codigoArticulo.trim(),
    descripcion: it.descripcion.trim(),
    cantidad: it.cantidad,
    unidadMedida: it.unidadMedida.trim() || 'UNI',
    nroItemMaster: it.nroItemMaster ?? idx + 1,
    ...(it.precioUnitario != null ? { precioUnitario: it.precioUnitario } : {}),
    ...(it.precioLista != null ? { precioLista: it.precioLista } : {}),
    ...(it.porcDesc != null ? { porcDesc: it.porcDesc } : {}),
    ...(it.pendienteFacturar != null ? { pendienteFacturar: it.pendienteFacturar } : {}),
  }));

const prepareDtoForApi = (dto: CreateOrdenReparacionDto): CreateOrdenReparacionDto => ({
  ...dto,
  items: prepareItemsForApi(dto.items ?? []),
});

const ordenReparacionService = {
  getOrdenes: async (params: OrdenReparacionSearchParams): Promise<OrdenReparacionPagedResult> => {
    const { signal, ...query } = params;
    const resp = await api.get('/api/OrdenesReparacion', {
      params: {
        SearchTerm: query.searchTerm || undefined,
        PageNumber: query.pageNumber ?? 1,
        PageSize: query.pageSize ?? 10,
        Estado: query.estado || undefined,
        Facturada: query.facturada != null ? query.facturada : undefined,
        FechaDesde: query.fechaDesde || undefined,
        FechaHasta: query.fechaHasta || undefined,
      },
      signal,
    });
    return normalizePagedOrdenes(resp.data, query);
  },

  getOrden: async (id: number | string): Promise<OrdenReparacionReadDto> => {
    const encoded = encodeURIComponent(String(id).trim());
    const resp = await api.get(`/api/OrdenesReparacion/${encoded}`);
    return normalizeOrden(resp.data as Record<string, unknown>);
  },

  createOrden: async (dto: CreateOrdenReparacionDto): Promise<OrdenReparacionReadDto> => {
    const resp = await api.post('/api/OrdenesReparacion', prepareDtoForApi(dto));
    return normalizeOrden(resp.data as Record<string, unknown>);
  },

  updateOrden: async (id: number | string, dto: UpdateOrdenReparacionDto): Promise<void> => {
    const encoded = encodeURIComponent(String(id).trim());
    await api.put(`/api/OrdenesReparacion/${encoded}`, prepareDtoForApi(dto));
  },

  deleteOrden: async (id: number | string): Promise<void> => {
    const encoded = encodeURIComponent(String(id).trim());
    await api.delete(`/api/OrdenesReparacion/${encoded}`);
  },

  getPdf: async (id: number | string): Promise<Blob> => {
    const encoded = encodeURIComponent(String(id).trim());
    const resp = await api.get(`/api/OrdenesReparacion/${encoded}/pdf`, {
      responseType: 'blob',
    });
    return resp.data;
  },

  getProximoNumero: async (serie = 'A'): Promise<string> => {
    const resp = await api.get('/api/OrdenesReparacion/proximo-numero', { params: { serie } });
    return normalizeProximoNumero(resp.data);
  },

  getPendientesFacturacion: async (nroCliente: number): Promise<VentaPendienteMaster[]> => {
    if (!nroCliente || nroCliente <= 0) return [];
    try {
      const resp = await api.get('/api/OrdenesReparacion/pendientes-facturacion', {
        params: { nroCliente },
      });
      const data = resp.data;
      const list = Array.isArray(data)
        ? data
        : Array.isArray((data as { items?: unknown[] })?.items)
          ? (data as { items: unknown[] }).items
          : Array.isArray((data as { Items?: unknown[] })?.Items)
            ? (data as { Items: unknown[] }).Items
            : [];
      if (list.length > 0 && list[0] && typeof list[0] === 'object' && 'nroFormularioMaster' in (list[0] as object)) {
        return list as VentaPendienteMaster[];
      }
      return list
        .map((row) => normalizeOrden(row as Record<string, unknown>))
        .flatMap((orden) => ordenTrabajoToPendientesMaster(orden));
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) return [];
      throw error;
    }
  },
};

export default ordenReparacionService;
